import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Edit, Mail, Phone, MapPin, User, PhoneCall, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Select, DatePicker, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import useIsMobile from '../../components/shared/useIsMobile';
import { getMemberProfile, updateMemberProfile } from '../../api/MemberProfile';
import { getOrganizationAttributes } from '../../api/OrganizationAttributes';
import { uploadFile } from '../../../admin/api/Upload/Image';
import IMAGES from '../../../../constant/images';
import './style.css';

const { Option } = Select;

/**
 * MyProfile Component
 * Displays member's profile information
 */
const MyProfile = () => {
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile(768);
  const [isEditMode, setIsEditMode] = useState(false);

  // Get profile data from Redux store
  const { data, loading, error } = useSelector((state) => state.memberProfile);
  const profile = data?.message?.payload;

  // Get organization attributes
  const { data: attributesData } = useSelector((state) => state.organizationAttributes);
  const attributes = attributesData?.message;

  // Form state for edit mode
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    countryCode: '+91',
    dateOfBirth: '',
    gender: '',
    country: 'India',
    state: '',
    pincode: '',
    city: '',
    location: '',
    emergencyContactName: '',
    emergencyContactNumber: '',
    emergencyContactRelationship: '',
    imageUrl: '',
  });

  // Organization data state - store IDs for the select dropdowns
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Available options from API
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);

  // Image upload state
  const [fileList, setFileList] = useState([]);

  // DatePicker state
  const [dobValue, setDobValue] = useState(null);

  // Fetch profile data and organization attributes on mount
  useEffect(() => {
    dispatch(getMemberProfile());
    dispatch(getOrganizationAttributes());
  }, [dispatch]);

  // Process attributes when they are loaded
  useEffect(() => {
    if (attributes) {
      // Department options
      if (attributes.DEPARTMENT) {
        setDepartmentOptions(
          attributes.DEPARTMENT.map(d => ({
            value: d.organizationAttributesId,
            label: d.attributeValue,
          }))
        );
      }

      // Location options
      if (attributes.LOCATION) {
        setLocationOptions(
          attributes.LOCATION.map(l => ({
            value: l.organizationAttributesId,
            label: l.attributeValue,
          }))
        );
      }

      // Group options
      if (attributes.GROUP) {
        setGroupOptions(
          attributes.GROUP.map(g => ({
            value: g.organizationAttributesId,
            label: g.attributeValue,
          }))
        );
      }
    }
  }, [attributes]);

  // Populate form when entering edit mode or when profile changes
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        mobile: profile.mobile || '',
        countryCode: profile.countryCode || '+91',
        dateOfBirth: profile.dateOfBirth || '',
        gender: profile.gender || '',
        country: profile.country || 'India',
        state: profile.state || '',
        pincode: profile.pincode || '',
        city: profile.city || '',
        location: profile.location || '',
        emergencyContactName: profile.emergencyContactName || '',
        emergencyContactNumber: profile.emergencyContactNumber || '',
        emergencyContactRelationship: profile.emergencyContactRelationship || '',
        imageUrl: profile.imageURL || '',
      });

      // Set organization data - extract IDs
      if (profile.departments) {
        setSelectedDepartments(
          profile.departments.map(d => d.attributeId)
        );
      }
      if (profile.locations) {
        setSelectedLocations(
          profile.locations.map(l => l.attributeId)
        );
      }
      if (profile.customGroups) {
        setSelectedGroups(
          profile.customGroups.map(g => g.attributeId)
        );
      }
    }
  }, [profile]);

  // Helper function to capitalize first letter
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Helper function to format comma-separated values from array
  const getCommaSeparatedValues = (arr) => {
    if (!Array.isArray(arr)) return '';
    return arr
      .map((item) => item?.attributeValue?.trim())
      .filter((val) => val)
      .join(', ');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      toast.error('You can only upload image files!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      toast.error('Image must be smaller than 5MB!');
      return false;
    }

    // Preview the image
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, imageUrl: reader.result }));
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  const handleSave = async () => {
    try {
      let imageUrl = formData.imageUrl;

      // If there's a new image file, upload it first
      if (fileList.length > 0 && fileList[0].originFileObj) {
        const formDataImage = new FormData();
        formDataImage.append('file', fileList[0].originFileObj);
        formDataImage.append('fileName', fileList[0].name);

        const uploadResult = await dispatch(uploadFile(formDataImage)).unwrap();

        if (uploadResult.status === 'success' && uploadResult.message?.payload) {
          imageUrl = uploadResult.message.payload;
        } else {
          toast.error('Failed to upload image');
          return;
        }
      }

      const updateData = {
        ...formData,
        imageUrl: imageUrl,
        departmentIds: selectedDepartments,
        clientLocationIds: selectedLocations,
        groupIds: selectedGroups,
      };

      const result = await dispatch(updateMemberProfile({
        userData: updateData,
        userId: profile?.pcUserId
      })).unwrap();

      if (result.status === 'success') {
        toast.success('Profile updated successfully!');
        dispatch(getMemberProfile()); // Refresh profile data
        setIsEditMode(false);
        setFileList([]);
      } else {
        toast.error(result.message || 'Failed to update profile');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    }
  };

  const handleCancel = () => {
    // Reset form to original profile data
    if (profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        mobile: profile.mobile || '',
        countryCode: profile.countryCode || '+91',
        dateOfBirth: profile.dateOfBirth || '',
        gender: profile.gender || '',
        country: profile.country || 'India',
        state: profile.state || '',
        pincode: profile.pincode || '',
        city: profile.city || '',
        location: profile.location || '',
        emergencyContactName: profile.emergencyContactName || '',
        emergencyContactNumber: profile.emergencyContactNumber || '',
        emergencyContactRelationship: profile.emergencyContactRelationship || '',
        imageUrl: profile.imageURL || '',
      });

      if (profile.departments) {
        setSelectedDepartments(profile.departments.map(d => d.attributeId));
      }
      if (profile.locations) {
        setSelectedLocations(profile.locations.map(l => l.attributeId));
      }
      if (profile.customGroups) {
        setSelectedGroups(profile.customGroups.map(g => g.attributeId));
      }
    }
    setFileList([]);
    setIsEditMode(false);
  };

  // Label-over-value field; "Not Available" values are muted so filled ones stand out
  const ProfileField = ({ label, value, wide }) => {
    const missing = !value || value === 'Not Available';
    return (
      <div className={`flex flex-col gap-1 min-w-0 ${wide ? 'sm:col-span-2' : ''}`}>
        <span className="text-[11px] uppercase tracking-wide font-semibold text-[#64748B] font-poppins">
          {label}
        </span>
        <span
          className={`text-sm font-poppins break-words ${
            missing ? 'text-[#94A3B8] font-normal' : 'text-[#171717] font-medium'
          }`}
        >
          {missing ? 'Not Available' : value}
        </span>
      </div>
    );
  };

  // Practitioner-style card with the #E7EDF5 header band + icon tile
  const SectionCard = ({ title, icon: Icon, tone, children }) => (
    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3 bg-[#E7EDF5]">
        <div className={`flex items-center justify-center w-[34px] h-[34px] rounded-xl bg-white flex-shrink-0 ${tone}`}>
          <Icon size={17} />
        </div>
        <h3 className="text-base font-semibold text-[#171717] font-poppins mb-0">{title}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 p-5">
        {children}
      </div>
    </div>
  );

  // Small icon + text pill used in the summary card
  const ContactPill = ({ icon: Icon, text }) =>
    text ? (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F8FAFC] border-[1px] border-solid border-[#F1F5F9] w-full min-w-0">
        <Icon size={15} className="text-[#2563EB] flex-shrink-0" />
        <span className="text-[13px] text-[#334155] font-poppins truncate">{text}</span>
      </div>
    ) : null;

  return (
    <>
      <div className="relative flex min-h-screen bg-[#F8FAFC]">
        <TopBar />

        {/* Sidebar - Only visible on desktop */}
        <div className={`hidden lg:flex ${collapsed ? 'w-[64px]' : 'w-[272px]'} fixed top-0 left-0 h-screen z-20`}>
          <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        </div>

        {/* Main Content */}
        <div
          className={`flex-1 min-w-0 bg-[#F8FAFC] pt-16 px-2 pb-8 transition-all duration-300 ml-0 ${collapsed ? 'lg:ml-[64px]' : 'lg:ml-[272px]'
            }`}
        >
          <div className="w-full h-full pt-2">
            <div className="w-full h-full">
              {/* Page Content */}
              <div className="my-profile-page bg-[#FFFFFF] min-h-screen rounded-2xl p-4 md:p-6 flex flex-col gap-6 md:gap-8">
                {/* Header */}
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">
                      {isEditMode ? 'Update Profile' : 'Profile'}
                    </h1>
                    {!isEditMode && (
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="flex items-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold font-poppins border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition"
                      >
                        <Edit size={16} className="text-[#171717]" />
                        <span>
                          Edit
                        </span>
                      </button>
                    )}
                  </div>
                  {/* Separator */}
                  <div className="h-px bg-[#F1F5F9] w-full"></div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563EB] mx-auto"></div>
                      <p className="mt-4 text-sm text-[#334155] font-poppins">Loading profile...</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <p className="text-base text-[#DC2626] font-poppins">Failed to load profile</p>
                      <p className="text-sm text-[#64748B] font-poppins mt-2">{error}</p>
                    </div>
                  </div>
                ) : profile ? (
                  !isEditMode ? (
                    /* VIEW MODE */
                    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">
                      {/* Summary Card */}
                      <div className="w-full lg:w-[300px] flex-shrink-0 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden">
                        <div className="h-20 bg-gradient-to-r from-[#0D62F8] to-[#00B8AB]" />
                        <div className="flex flex-col items-center px-5 pb-5 -mt-12">
                          <div className="w-24 h-24 rounded-2xl bg-white p-1 shadow-md">
                            <div className="w-full h-full rounded-xl bg-[#EFF6FF] flex items-center justify-center overflow-hidden">
                              {profile.imageURL ? (
                                <img
                                  src={profile.imageURL}
                                  alt="Profile"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <img
                                  src={IMAGES.user_filled}
                                  alt="Default Profile"
                                  className="w-14 h-14 object-contain"
                                />
                              )}
                            </div>
                          </div>
                          <h2 className="mt-3 mb-0 text-lg font-semibold text-[#171717] font-poppins text-center break-words">
                            {`${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Not Available'}
                          </h2>
                          {(profile.gender || profile.dateOfBirth) && (
                            <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                              {profile.gender && (
                                <span className="px-2.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#2563EB] text-[12px] font-semibold font-poppins">
                                  {capitalize(profile.gender)}
                                </span>
                              )}
                              {profile.dateOfBirth && (
                                <span className="px-2.5 py-0.5 rounded-full bg-[#F0FDFA] text-[#0D9488] text-[12px] font-semibold font-poppins">
                                  {profile.dateOfBirth}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full mt-4">
                            <ContactPill icon={Mail} text={profile.email} />
                            <ContactPill
                              icon={Phone}
                              text={profile.mobile ? `${profile.countryCode || '+91'} ${profile.mobile}` : null}
                            />
                            <ContactPill
                              icon={MapPin}
                              text={[profile.city, profile.state, profile.country || 'India'].filter(Boolean).join(', ')}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Detail Cards */}
                      <div className="flex flex-col gap-4 flex-1 min-w-0 w-full">
                        <SectionCard title="Personal Information" icon={User} tone="text-[#2563EB]">
                          <ProfileField
                            label="Name"
                            value={`${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Not Available'}
                          />
                          <ProfileField
                            label="Email ID"
                            value={profile.email || 'Not Available'}
                          />
                          <ProfileField
                            label="Phone Number"
                            value={profile.mobile ? `${profile.countryCode || '+91'} ${profile.mobile}` : 'Not Available'}
                          />
                          <ProfileField
                            label="DOB"
                            value={profile.dateOfBirth || 'Not Available'}
                          />
                          <ProfileField
                            label="Gender"
                            value={capitalize(profile.gender) || 'Not Available'}
                          />
                          <ProfileField
                            label="Country"
                            value={profile.country || 'India'}
                          />
                          <ProfileField
                            label="State"
                            value={profile.state || 'Not Available'}
                          />
                          <ProfileField
                            label="City"
                            value={profile.city || 'Not Available'}
                          />
                          <ProfileField
                            label="Pincode"
                            value={profile.pincode || 'Not Available'}
                          />
                          <ProfileField
                            label="Address"
                            value={profile.location || 'Not Available'}
                          />
                        </SectionCard>

                        {/* Emergency Contact Details */}
                        <SectionCard title="Emergency Contact Details" icon={PhoneCall} tone="text-[#E11D48]">
                          <ProfileField
                            label="Name"
                            value={profile.emergencyContactName || 'Not Available'}
                          />
                          <ProfileField
                            label="Contact Number"
                            value={profile.emergencyContactNumber || 'Not Available'}
                          />
                          <ProfileField
                            label="Relationship"
                            value={capitalize(profile.emergencyContactRelationship) || 'Not Available'}
                          />
                        </SectionCard>

                        {/* Organisation Details */}
                        <SectionCard title="Organisation Details" icon={Building2} tone="text-[#0D9488]">
                          <ProfileField
                            label="Department"
                            value={getCommaSeparatedValues(profile.departments) || 'Not Available'}
                          />
                          <ProfileField
                            label="Location"
                            value={getCommaSeparatedValues(profile.locations) || 'Not Available'}
                          />
                          <ProfileField
                            label="Custom Groups"
                            value={getCommaSeparatedValues(profile.customGroups) || 'Not Available'}
                            wide
                          />
                        </SectionCard>
                      </div>
                    </div>
                  ) : (
                    /* EDIT MODE */
                    <div className="flex flex-col lg:flex-row gap-6 lg:gap-[112px]">
                      {/* Left Column - Profile Picture */}
                      <div className="flex flex-col items-center lg:items-start gap-4 lg:gap-8">
                        <div className="flex flex-col gap-4 items-center lg:items-start">
                          <p className="text-sm font-medium text-[#334155] font-poppins leading-6">Profile Picture</p>
                          <div className="bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] p-2 rounded-2xl flex items-center justify-center w-32 h-32 md:w-40 md:h-40">
                            {formData.imageUrl ? (
                              <img
                                src={formData.imageUrl}
                                alt="Profile"
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <img
                                src={IMAGES.user_filled}
                                alt="Default Profile"
                                className="w-20 h-20 md:w-22 md:h-22 object-contain"
                              />
                            )}
                          </div>
                        </div>
                        <Upload
                          fileList={fileList}
                          onChange={handleUploadChange}
                          beforeUpload={beforeUpload}
                          accept="image/*"
                          maxCount={1}
                          showUploadList={false}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            className="flex items-center justify-center gap-1 bg-white border-[1px] border-solid border-[#E2E8F0] text-[#171717] hover:bg-[#F8FAFC] rounded-xl shadow-sm h-9 w-40 font-poppins font-semibold text-[13px]"
                          >
                            Upload Picture
                          </Button>
                        </Upload>
                      </div>

                      {/* Right Column - Form Fields */}
                      <div className="flex-1 flex flex-col gap-4 md:gap-6">
                        {/* Name and Email Row */}
                        <div className="flex flex-col md:flex-row gap-4 md:gap-[56px]">
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Name</label>
                            <input
                              type="text"
                              value={`${formData.firstName} ${formData.lastName}`.trim()}
                              onChange={(e) => {
                                const names = e.target.value.split(' ');
                                setFormData(prev => ({
                                  ...prev,
                                  firstName: names[0] || '',
                                  lastName: names.slice(1).join(' ') || '',
                                }));
                              }}
                              placeholder="Type here"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Email ID</label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="Type here"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                        </div>

                        {/* Phone and DOB Row */}
                        <div className="flex flex-col md:flex-row gap-4 md:gap-[56px]">
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">
                              Phone Number<span className="text-[#EC3234]">*</span>
                            </label>
                            <input
                              type="tel"
                              name="mobile"
                              value={formData.mobile}
                              onChange={handleInputChange}
                              placeholder="Type here"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">DOB</label>
                            <DatePicker
                              className="w-full h-10 rounded-xl bg-white shadow-sm font-poppins"
                              style={{ border: "1px solid #E2E8F0" }}
                              onChange={(date, dateString) => {
                                setDobValue(dateString);
                                setFormData(prev => ({ ...prev, dateOfBirth: dateString }));
                              }}
                              disabledDate={(current) => current && current > moment().endOf('day')}
                            />
                          </div>
                        </div>

                        {/* Gender */}
                        <div className="flex flex-col gap-3 w-full md:w-[400px]">
                          <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Gender</label>
                          <Select
                            value={formData.gender}
                            onChange={(value) => setFormData(prev => ({ ...prev, gender: value }))}
                            placeholder="Select"
                          >
                            <Option value="MALE">Male</Option>
                            <Option value="FEMALE">Female</Option>
                            <Option value="OTHER">Other</Option>
                          </Select>
                        </div>

                        {/* Country and State Row */}
                        <div className="flex flex-col md:flex-row gap-4 md:gap-[56px]">
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">
                              Country<span className="text-[#EC3234]">*</span>
                            </label>
                            <select
                              name="country"
                              value={formData.country}
                              onChange={handleInputChange}
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF] appearance-none"
                              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%2364748B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center' }}
                            >
                              <option value="India">India</option>
                            </select>
                          </div>
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">
                              State<span className="text-[#EC3234]">*</span>
                            </label>
                            <input
                              type="text"
                              name="state"
                              value={formData.state}
                              onChange={handleInputChange}
                              placeholder="Select State"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                        </div>

                        {/* Pincode and City Row */}
                        <div className="flex flex-col md:flex-row gap-4 md:gap-[56px]">
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">
                              Pincode<span className="text-[#EC3234]">*</span>
                            </label>
                            <input
                              type="text"
                              name="pincode"
                              value={formData.pincode}
                              onChange={handleInputChange}
                              placeholder="Type here"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">
                              City<span className="text-[#EC3234]">*</span>
                            </label>
                            <input
                              type="text"
                              name="city"
                              value={formData.city}
                              onChange={handleInputChange}
                              placeholder="Select City"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                        </div>

                        {/* Address */}
                        <div className="flex flex-col gap-3 w-full">
                          <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Address</label>
                          <textarea
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            placeholder="Type here"
                            rows={4}
                            className="px-4 py-3 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF] resize-none"
                          />
                        </div>

                        {/* Emergency Contact Details */}
                        <h3 className="text-base font-semibold text-[#171717] font-poppins mt-2 md:mt-4 mb-0 pt-4 border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9]">Emergency Contact Details</h3>

                        <div className="flex flex-col md:flex-row gap-4 md:gap-[56px]">
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Name</label>
                            <input
                              type="text"
                              name="emergencyContactName"
                              value={formData.emergencyContactName}
                              onChange={handleInputChange}
                              placeholder="Type here"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">
                              Phone Number<span className="text-[#EC3234]">*</span>
                            </label>
                            <input
                              type="tel"
                              name="emergencyContactNumber"
                              value={formData.emergencyContactNumber}
                              onChange={handleInputChange}
                              placeholder="Type here"
                              className="px-4 h-10 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-sm font-poppins text-[#171717] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-3 w-full md:w-[400px]">
                          <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Relationship</label>
                          <Select
                            value={formData.emergencyContactRelationship}
                            onChange={(value) => setFormData(prev => ({ ...prev, emergencyContactRelationship: value }))}
                            placeholder="Select"
                          >
                            <Option value="PARENT">Parent</Option>
                            <Option value="SIBLING">Sibling</Option>
                            <Option value="SPOUSE">Spouse</Option>
                            <Option value="FRIEND">Friend</Option>
                            <Option value="OTHER">Other</Option>
                          </Select>
                        </div>

                        {/* Organisation Details */}
                        <h3 className="text-base font-semibold text-[#171717] font-poppins mt-2 md:mt-4 mb-0 pt-4 border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9]">Organisation Details</h3>

                        <div className="flex flex-col md:flex-row gap-4 md:gap-[56px]">
                          {/* Department */}
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Department</label>
                            <Select
                              mode="multiple"
                              allowClear
                              value={selectedDepartments}
                              onChange={setSelectedDepartments}
                              placeholder="Select your department"
                              options={departmentOptions}
                              dropdownClassName="custom-dropdown"
                            />
                          </div>

                          {/* Location */}
                          <div className="flex flex-col gap-3 w-full md:w-[400px]">
                            <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Location</label>
                            <Select
                              mode="multiple"
                              allowClear
                              value={selectedLocations}
                              onChange={setSelectedLocations}
                              placeholder="Select your Location"
                              options={locationOptions}
                              dropdownClassName="custom-dropdown"
                            />
                          </div>
                        </div>

                        {/* Custom Groups */}
                        <div className="flex flex-col gap-3 w-full md:w-[400px]">
                          <label className="text-sm font-medium text-[#334155] font-poppins leading-6">Custom Groups</label>
                          <Select
                            mode="multiple"
                            allowClear
                            value={selectedGroups}
                            onChange={setSelectedGroups}
                            placeholder="Select your groups"
                            options={groupOptions}
                            dropdownClassName="custom-dropdown"
                          />
                        </div>

                        {/* Save and Cancel buttons at bottom */}
                        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-4 md:mt-6 pt-4 border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9]">
                          <button
                            onClick={handleCancel}
                            className="w-full sm:w-auto h-9 px-[14px] sm:px-6 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-[13px] font-semibold text-[#334155] font-poppins hover:bg-[#F8FAFC] transition"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSave}
                            disabled={loading}
                            className="w-full sm:w-auto h-9 px-[14px] sm:px-6 btn-gradient border-0 rounded-xl text-[13px] font-semibold font-poppins disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                ) : null}

                {/* Bottom Separator */}
                <div className="h-px bg-[#F1F5F9] w-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyProfile;
